import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Spinner, Alert, Card, Button } from 'react-bootstrap';
import {
  getProjetoWithUsersAndTarefasAPI,
  updateProjetoStatusAPI,
} from 'api/requestsApi';
import ProjetoDetailsTable from 'components/Projeto/ProjetoDetailsTable';
import { ProjetoWithUsersAndTarefasDTO } from 'types/projeto';
import ProjetoTarefasTable from 'components/Projeto/ProjetoTarefasTable';
import ProjetoTarefasArquivadasTable from 'components/Projeto/ProjetoTarefasArquivadasTable';
import ProjetoTarefaModal from 'components/Tarefa/ProjetoTarefaModal';
import TarefaModal from 'components/Tarefa/TarefaModal';
import {
  TarefaInsertFormData,
  TarefaUpdateFormData,
  TarefaWithUserAndProjetoDTO,
} from 'types/tarefa';
import {
  addTarefa,
  arquivarTarefa,
  getTarefaWithUsersAndProjeto,
  updateTarefa,
} from 'services/tarefaService';
import { arquivarProjeto, reativarProjeto } from 'services/projetoService';
import { toast } from 'react-toastify';
import BackButton from 'components/Shared/BackButton/BackButton';
import ProjetoExternosManager from 'components/Projeto/ProjetoExternosManager';
import ProjetoFasesManager from 'components/Projeto/ProjetoFasesManager';
import { ProjetoFase } from 'types/projetoFase';
import { getFasesByProjeto } from 'services/projetoFaseService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faColumns,
  faBoxArchive,
  faBoxOpen,
} from '@fortawesome/free-solid-svg-icons';
import { usePermissions } from 'hooks/usePermissions';
import { Permission } from 'permissions/rolePermissions';
import './projetoDetailsPage.scss';

const ProjetoDetailsPage: React.FC = () => {
  const { projetoId } = useParams<{ projetoId: string }>();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canArchive = hasPermission(Permission.EDIT_PROJECT);
  const [projeto, setProjeto] = useState<ProjetoWithUsersAndTarefasDTO | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fases, setFases] = useState<ProjetoFase[]>([]);

  // Estados para o modal de nova tarefa
  const [showTarefaModal, setShowTarefaModal] = useState(false);

  // Estados para o modal de edição de tarefa
  const [showEditTarefaModal, setShowEditTarefaModal] = useState(false);
  const [tarefaToEdit, setTarefaToEdit] =
    useState<TarefaWithUserAndProjetoDTO | null>(null);

  const fetchProjeto = async () => {
    if (projetoId) {
      setIsLoading(true);
      try {
        const fetchedProjeto = await getProjetoWithUsersAndTarefasAPI(
          Number(projetoId)
        );
        setProjeto(fetchedProjeto);
        setError(null);
      } catch (error) {
        console.error('Error fetching projeto details:', error);
        setError('Erro ao carregar detalhes do projeto');
        toast.error('Erro ao carregar detalhes do projeto');
      } finally {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchProjeto();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projetoId]);

  // Loaded apart from fetchProjeto, so phase edits don't trigger its full-page spinner.
  useEffect(() => {
    if (!projetoId) return;
    getFasesByProjeto(Number(projetoId))
      .then(setFases)
      .catch((error) => {
        console.error('Erro ao carregar fases:', error);
        setFases([]);
      });
  }, [projetoId]);

  // Mirrors the backend: deleting a phase sends its tarefas back to "sem fase".
  const handleFaseDeleted = (faseId: number) => {
    setProjeto((prev) =>
      prev
        ? {
            ...prev,
            tarefas: prev.tarefas.map((tarefa) =>
              tarefa.faseId === faseId ? { ...tarefa, faseId: null } : tarefa
            ),
          }
        : prev
    );
  };

  // Handler para adicionar nova tarefa
  const handleAddTarefa = async (formData: TarefaInsertFormData) => {
    try {
      await addTarefa(formData);
      toast.success('Tarefa criada com sucesso!');
      // Recarregar os dados do projeto para mostrar a nova tarefa
      await fetchProjeto();
    } catch (error) {
      console.error('Erro ao adicionar tarefa:', error);

      // Verificar se é um erro específico de validação de prazo
      if (error instanceof Error && error.message.includes('prazo')) {
        toast.error(error.message);
      } else {
        toast.error('Erro ao adicionar tarefa');
      }
    }
  };

  // Handler para abrir o modal de edição de uma tarefa existente
  // /projetos/{id}/full embeds plain TarefaDTOs (no users/projeto), despite
  // being typed as Tarefa[]; TarefaModal needs the full task, so load it on click
  const handleEditTarefa = async (tarefaId: number) => {
    try {
      setTarefaToEdit(await getTarefaWithUsersAndProjeto(tarefaId));
      setShowEditTarefaModal(true);
    } catch (error) {
      console.error('Erro ao carregar tarefa:', error);
      toast.error('Erro ao carregar a tarefa');
    }
  };

  // Handler para gravar a edição de uma tarefa
  const handleUpdateTarefa = async (formData: TarefaUpdateFormData) => {
    try {
      await updateTarefa(formData.id, formData);
      toast.success('Tarefa atualizada com sucesso!');
      setShowEditTarefaModal(false);
      setTarefaToEdit(null);
      await fetchProjeto();
    } catch (error) {
      console.error('Erro ao atualizar tarefa:', error);
      if (error instanceof Error && error.message.includes('prazo')) {
        toast.error(error.message);
      } else {
        toast.error('Erro ao atualizar tarefa');
      }
    }
  };

  // Archive a DONE tarefa from the edit modal. fetchProjeto shows the
  // full-page spinner, which remounts ProjetoTarefasArquivadasTable so it
  // re-fetches and lists the newly archived tarefa
  const handleArchiveTarefa = async (tarefaId: number) => {
    try {
      await arquivarTarefa(tarefaId);
      toast.success('Tarefa arquivada com sucesso!');
      setShowEditTarefaModal(false);
      setTarefaToEdit(null);
      await fetchProjeto();
    } catch (error) {
      console.error('Erro ao arquivar tarefa:', error);
      toast.error(
        error instanceof Error ? error.message : 'Erro ao arquivar tarefa'
      );
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (projetoId && projeto) {
      try {
        await updateProjetoStatusAPI(Number(projetoId), newStatus);
        await fetchProjeto();
        toast.success('Status do projeto atualizado com sucesso!');
      } catch (error) {
        console.error('Error updating projeto status:', error);
        toast.error('Erro ao atualizar status do projeto');
      }
    }
  };

  const handleArchiveProjeto = async () => {
    if (!projetoId) return;
    try {
      await arquivarProjeto(Number(projetoId));
      await fetchProjeto();
      toast.success('Projeto arquivado com sucesso');
    } catch (error) {
      console.error('Error archiving project:', error);
      const message =
        error instanceof Error ? error.message : 'Erro ao arquivar projeto';
      toast.error(message);
    }
  };

  const handleReactivateProjeto = async () => {
    if (!projetoId) return;
    try {
      await reativarProjeto(Number(projetoId));
      await fetchProjeto();
      toast.success('Projeto reativado com sucesso');
    } catch (error) {
      console.error('Error reactivating project:', error);
      toast.error('Erro ao reativar projeto');
    }
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Carregando...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  if (!projeto) {
    return (
      <Container className="mt-4">
        <Alert variant="warning">Projeto não encontrado</Alert>
      </Container>
    );
  }

  return (
    <div className="page-container projeto-details-container">
      <div className="page-shell">
        <div className="page-title-container">
          <h2 className="page-title">Detalhes do Projeto</h2>
          <div className="page-actions">
            <BackButton to="/projetos" />
            <Button
              variant="primary"
              onClick={() => setShowTarefaModal(true)}
            >
              <FontAwesomeIcon icon={faPlus} className="me-2" />
              Nova Tarefa
            </Button>
            <Button
              variant="outline-primary"
              onClick={() => navigate(`/projetos/${projetoId}/full`)}
            >
              <FontAwesomeIcon icon={faColumns} className="me-2" />
              Kanban
            </Button>
            {canArchive && !projeto.arquivadaEm && projeto.status === 'CONCLUIDO' && (
              <Button variant="outline-secondary" onClick={handleArchiveProjeto}>
                <FontAwesomeIcon icon={faBoxArchive} className="me-2" />
                Arquivar
              </Button>
            )}
            {canArchive && projeto.arquivadaEm && (
              <Button variant="outline-secondary" onClick={handleReactivateProjeto}>
                <FontAwesomeIcon icon={faBoxOpen} className="me-2" />
                Reativar
              </Button>
            )}
          </div>
        </div>

        <div className="mb-4">
          <div className="details-table-wrapper">
            <ProjetoDetailsTable
              projeto={projeto}
              onStatusChange={handleStatusChange}
            />
          </div>
        </div>

        <div className="mb-4">
          <ProjetoFasesManager
            projetoId={projeto.id}
            prazoProjeto={projeto.prazo}
            fases={fases}
            tarefas={projeto.tarefas ?? []}
            canEdit={canArchive}
            onFasesChange={setFases}
            onFaseDeleted={handleFaseDeleted}
            onEditTarefa={handleEditTarefa}
          />
        </div>

        <div className="mb-4">
          <Card>
            <Card.Header as="h5">Tarefas Associadas</Card.Header>
            <Card.Body className="p-0">
              <div className="details-table-wrapper">
                <ProjetoTarefasTable
                  tarefas={projeto.tarefas}
                  onEditTarefa={handleEditTarefa}
                  fases={fases}
                />
              </div>
            </Card.Body>
          </Card>
        </div>

        <div className="mb-4">
          <Card>
            <Card.Header as="h5">Tarefas Arquivadas</Card.Header>
            <Card.Body className="p-0">
              <div className="details-table-wrapper">
                <ProjetoTarefasArquivadasTable
                  projetoId={projeto.id}
                  onReactivated={fetchProjeto}
                />
              </div>
            </Card.Body>
          </Card>
        </div>

        {/* Substitua a seção anterior de Colaboradores Externos pelo ProjetoExternosManager */}
        <div className="mb-4">
          {projeto.id && (
            <ProjetoExternosManager
              projetoId={projeto.id}
              onUpdate={fetchProjeto}
            />
          )}
        </div>
      </div>

      {showTarefaModal && (
        <ProjetoTarefaModal
          show={showTarefaModal}
          onHide={() => setShowTarefaModal(false)}
          onSave={handleAddTarefa}
          projetoId={projeto.id}
        />
      )}

      {showEditTarefaModal && (
        <TarefaModal
          show={showEditTarefaModal}
          onHide={() => {
            setShowEditTarefaModal(false);
            setTarefaToEdit(null);
          }}
          onSave={(formData) =>
            handleUpdateTarefa(formData as TarefaUpdateFormData)
          }
          onArchive={handleArchiveTarefa}
          isEditing
          tarefa={tarefaToEdit}
        />
      )}
    </div>
  );
};

export default ProjetoDetailsPage;
